import { Component, HostBinding, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { SJIService } from '../../services/sji.service';
import { SJI_API_URLS } from '../../_configs/webapiurls';
import { SJIAppContextService } from '../../../core/services/system/sji.appcontext.service';
import { searchModel } from '../../order/models/searchModel';

/**
 * Left-side promotion bar shown on warehouse / factory direct order pages.
 * Eligible companies: those with Module SPB (id=20) enabled, or DTI
 * (CompanyId=2) as a transitional fallback so the feature works for DTI
 * without requiring per-customer CompanyCustomerModule rows.
 * The host is only visible when the feature is permitted AND the API returns
 * at least one active promotion — portals with no configured promotions
 * collapse the slot entirely rather than showing an empty placeholder.
 * Auto-cycles to the next page of items every CYCLE_MS when overflowing the visible area.
 */
@Component({
  selector: 'side-promotion-bar',
  templateUrl: './sidePromotionBar.component.html',
  styleUrls: ['./sidePromotionBar.component.scss']
})
export class SidePromotionBarComponent implements OnInit, OnChanges, OnDestroy {
  /** 1 = Warehouse Order, 2 = Factory Direct Order. */
  @Input() orderType: number = 1;

  public items: SidePromotionItem[] = [];
  public visible = false;
  public pageIndex = 0;
  public itemsPerPage = 4;

  @HostBinding('style.display') get hostDisplay(): string {
    return this.visible ? 'block' : 'none';
  }

  private cycleTimer: any = null;
  private hovered = false;
  private static readonly CYCLE_MS = 5000;
  private static readonly MODULE_SPB = 20;
  private static readonly COMPANY_DTI = 2;

  constructor(
    private appContextService: SJIAppContextService,
    private sjiService: SJIService,
    private sanitizer: DomSanitizer,
    private router: Router) { }

  ngOnInit() {
    if (this.isFeatureEnabled()) this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderType'] && !changes['orderType'].firstChange && this.isFeatureEnabled()) {
      this.load();
    }
  }

  ngOnDestroy() { this.stopCycle(); }

  private isFeatureEnabled(): boolean {
    const ctx = this.appContextService.getAppContext();
    const hasModule = !!(ctx && ctx.modules && ctx.modules.indexOf(SidePromotionBarComponent.MODULE_SPB) > -1);
    const isDti = !!(ctx && Number(ctx.companyId) === SidePromotionBarComponent.COMPANY_DTI);
    return hasModule || isDti;
  }

  private load() {
    const url = `${SJI_API_URLS.sidePromotion_list}?orderType=${this.orderType}`;
    this.sjiService.get(url).subscribe(data => {
      this.items = (data as SidePromotionItem[]) || [];
      this.pageIndex = 0;
      this.visible = this.items.length > 0;
      if (this.visible) {
        this.startCycle();
      } else {
        this.stopCycle();
      }
    });
  }

  /** Total pages when items overflow the visible scope. */
  public get pageCount(): number {
    if (!this.items || this.items.length === 0) return 0;
    return Math.ceil(this.items.length / this.itemsPerPage);
  }

  public get visibleItems(): SidePromotionItem[] {
    if (!this.items || this.items.length === 0) return [];
    if (this.items.length <= this.itemsPerPage) return this.items;
    const start = this.pageIndex * this.itemsPerPage;
    return this.items.slice(start, start + this.itemsPerPage);
  }

  public onMouseEnter() { this.hovered = true; }
  public onMouseLeave() { this.hovered = false; }

  public goPage(i: number) {
    if (this.pageCount === 0) return;
    this.pageIndex = ((i % this.pageCount) + this.pageCount) % this.pageCount;
  }

  public titleHtml(item: SidePromotionItem): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(item.title || '');
  }

  public descriptionHtml(item: SidePromotionItem): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(item.description || '');
  }

  public youTubeUrl(item: SidePromotionItem): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(item.youTubeEmbedUrl || '');
  }

  /**
   * Click-through. Mirrors the existing PromotionComponent.productsSearch behaviour:
   * route to the matching order page with searchModel pre-populated.
   */
  public clickItem(item: SidePromotionItem) {
    if (!item.isShowLink) return;
    const search: searchModel = {
      pageIndex: null, pageSize: null, keywords: null,
      code: item.navItemCd || null,
      size: null,
      sortBy: null,
      brandId: item.itemBrandCd || null,
      description: null,
      categoryId: item.itemCategoryCd || null,
      subCategoryId: item.itemSubCategoryCd || null,
      filters: null
    };
    const route = this.orderType === 2 ? 'order/directContainer' : 'order/index';
    this.router.navigate([route], { queryParams: search });
  }

  private startCycle() {
    this.stopCycle();
    if (this.pageCount <= 1) return;
    this.cycleTimer = setInterval(() => {
      if (!this.hovered) this.goPage(this.pageIndex + 1);
    }, SidePromotionBarComponent.CYCLE_MS);
  }

  private stopCycle() {
    if (this.cycleTimer) { clearInterval(this.cycleTimer); this.cycleTimer = null; }
  }
}

export interface SidePromotionItem {
  id: string;
  title: string;
  description: string;
  mediaType: number;
  imagePath: string;
  src: string;
  youTubeEmbedUrl: string;
  startDate: Date;
  endDate: Date;
  itemCd: string;
  navItemCd: string;
  itemBrandCd: string;
  itemCategoryCd: string;
  itemSubCategoryCd: string;
  displayOrder: number;
  isShowLink: boolean;
}
